import { ExclamationCircleIcon } from '@heroicons/react/20/solid';
import React, { useEffect, useState } from 'react';

export default function ProductIdInput({ productId, setProductId, location, setHistoryData }) {
    const validLengths = [9, 10, 12, 13];
    const [debounceTimer, setDebounceTimer] = useState(null);
    const [showError, setShowError] = useState(false); // New state for error visibility
    const [errorMsg, setErrorMsg] = useState(''); // specific message (SFP rejection, etc.)

    // Y/Z bins are Seller Fulfilled Prime sections — UPC-only intake.
    const isSfpSection = /^[yz]/i.test((location || '').trim());

    const handleProductIdChange = (e) => {
        const value = e.target.value;
        setProductId(value);

        // Clear error when input changes
        setShowError(false);
        setErrorMsg('');

        if (debounceTimer) {
            clearTimeout(debounceTimer);
        }

        const timer = setTimeout(() => {
            processProductId(value);
        }, 1000);

        setDebounceTimer(timer);
    };

    const processProductId = async (value) => {
        if (!location) {
            console.warn("Location must be selected before submitting a product ID.");
            return;
        }

        // Show error if value exists and length is invalid
        if (value && !validLengths.includes(value.length)) {
            setShowError(true);
            return;
        }

        if (validLengths.includes(value.length)) {
            const successTone = new Audio('/sounds/success-tone.mp3');
            const errorTone = new Audio('/sounds/error-tone.mp3');

            if (value.startsWith("LPN")) {
                errorTone.play();
                setProductId('');
                setErrorMsg('LPN codes are not accepted here');
                setShowError(true);
                return;
            }

            // SFP sections accept UPC codes only. Enforce the format client-side for
            // immediate feedback; the server also verifies the UPC exists in the price list.
            if (isSfpSection && !/^\d{12,14}$/.test(value)) {
                errorTone.play();
                setProductId('');
                setErrorMsg('SFP sections (Y/Z) accept UPC codes only (12-14 digits)');
                setShowError(true);
                return;
            }

            try {
                const token = localStorage.getItem('jwt');

                const submitProductId = async () => {
                    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/sfinventory`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({ productId: value, locationId: location }),
                    });

                    if (!response.ok) {
                        let msg = 'Failed to submit product ID';
                        try {
                            const body = await response.json();
                            if (body && body.message) msg = body.message;
                        } catch (_) { /* non-JSON body */ }
                        throw new Error(msg);
                    }
                };

                const updateHistory = async () => {
                    try {
                        const historyResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/updatesflocalhistory`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify({
                                productId: value,
                                locationId: location
                            }),
                        });

                        if (!historyResponse.ok) {
                            throw new Error('Failed to update local history');
                        }

                        const newHistoryData = await historyResponse.json();
                        const convertedData = newHistoryData.map(item => {
                            const utcCreatedDate = new Date(item.created_at + 'Z');
                            const utcUpdatedDate = item.updated_at ? new Date(item.updated_at + 'Z') : null;

                            const localCreatedDate = utcCreatedDate.toLocaleString(undefined, {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                                hour12: false
                            });

                            const localUpdatedDate = utcUpdatedDate
                                ? utcUpdatedDate.toLocaleString(undefined, {
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    second: '2-digit',
                                    hour12: false
                                })
                                : null;

                            return {
                                ...item,
                                created_at: localCreatedDate,
                                updated_at: localUpdatedDate
                            };
                        });

                        setHistoryData(convertedData);
                    } catch (error) {
                        console.error('Error updating history:', error);
                        throw error;
                    }
                };

                await submitProductId();
                await updateHistory();

                successTone.play();
                setProductId('');
                setShowError(false); // Clear error on success
            } catch (error) {
                console.error('Error:', error);
                errorTone.play();
                setErrorMsg(error.message || '');
                setShowError(true); // Show error on failure
            }
        }
    };

    useEffect(() => {
        return () => {
            if (debounceTimer) {
                clearTimeout(debounceTimer);
            }
        };
    }, [debounceTimer]);

    return (
        <div className="relative flex flex-col w-[400px]">
            <label htmlFor="product-id" className="text-sm font-medium text-gray-900">
                UPC, EAN, ASIN, SKU, or FNSKU
            </label>
            <div className="relative mt-1 rounded-md shadow-sm">
                <input
                    id="product-id"
                    type="text"
                    placeholder="Enter Product ID"
                    value={productId}
                    onChange={handleProductIdChange}
                    className={`block w-full h-[38px] rounded-md border-0 py-1.5 pr-10 ${
                        showError ? 'text-red-900 ring-red-300' : 'text-gray-900 ring-gray-300'
                    } ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset ${
                        showError ? 'focus:ring-red-500' : 'focus:ring-indigo-500'
                    } sm:text-sm sm:leading-6`}
                />
                {showError && (
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                        <ExclamationCircleIcon aria-hidden="true" className="h-5 w-5 text-red-500" />
                    </div>
                )}
            </div>
            {showError && (
                <p className="absolute -bottom-10 text-sm text-red-600">
                    {errorMsg || 'Not a valid UPC, EAN, ASIN, SKU, or FNSKU'}
                </p>
            )}
        </div>
    );
}